/* 4over-parity product configurator — free, no apps.
 * Reads a compact combo/price JSON (built by print-store/configurator/transform.py,
 * hosted on S3/CloudFront), renders cascading selects that only permit valid
 * combinations, shows the run-size price grid, and adds the job to cart at the
 * exact configured price via a hidden penny-priced "Print Charge" product.
 */
(function () {
  var mount = document.getElementById('cfg-app');
  if (!mount) return;

  var DATA_BASE = mount.dataset.base;            // e.g. https://cdn.../print-configurator
  var CFG_HANDLE = mount.dataset.cfg;            // e.g. common-menus
  var PRODUCT_VARIANT = mount.dataset.variant;   // single $0 variant id of this product
  var CHARGE_VARIANT = mount.dataset.charge;     // variant id of $0.01 Print Charge product
  var CHARGE_100 = mount.dataset.charge100;      // $100 denomination Print Charge
  var CHARGE_1 = mount.dataset.charge1;          // $1 denomination Print Charge
  var money = function (n) { return '$' + n.toFixed(2); };

  // Plain-English help so shoppers understand print jargon.
  // ATTR_HELP = what a whole option group means; OPT_HELP = what a specific choice means.
  var ATTR_HELP = {
    'Size': 'The flat, unfolded size of your piece — width × height, in inches.',
    'Stock': 'The paper or material itself. "Cover" / "C2S" / higher PT = thick, stiff card; "Book" / "Text" / higher LB = lighter flyer paper (bigger number = thicker). Specialty materials (coroplast, aluminum, vinyl, styrene) are for signs and banners.',
    'Cover Stock': 'The paper used for a booklet’s cover — usually thicker and sturdier than the inside pages.',
    'Colorspec': 'Which sides print in full color. 4/4 = full color on BOTH sides · 4/0 = full color FRONT only, blank back · 4/1 = full color front, black-ink-only back.',
    'Coating': 'A thin finish applied over the ink. Aqueous = light satin protection · UV / Gloss = high shine, very durable · Matte / Satin = smooth, low-glare · No Coating = natural surface you can write on.',
    'Cover Coating': 'The protective finish applied to a booklet’s cover.',
    'Lamination': 'A plastic film bonded to the surface for durability and a premium feel. Gloss = shiny · Silk / Velvet / Matte = smooth, soft-touch, non-glare · No Lamination = uncoated.',
    'SPOT UV SIDES': 'A glossy, clear coating applied only to SELECTED areas (like a logo) so they pop against a matte background. Choose which side(s) get it.',
    'Raised Spot UV Side': 'A thick, raised glossy coating on chosen areas that you can feel with your fingers.',
    'Raised Spot UV Height': 'How raised (pronounced) the glossy spot-UV coating feels.',
    'Raised Foil Side': 'A raised metallic-foil accent applied to the chosen side.',
    'Foil Color': 'A shiny metallic foil stamped onto the piece as an accent.',
    'Edge Color': 'A painted color applied to the cut edges of thick cards for a designer look.',
    'Folding Options': 'How the flat sheet is folded into its final shape — see the note under your choice.',
    'Score and Fold': 'A “score” is a pressed crease so thick stock folds cleanly without cracking; this sets how it’s scored and folded.',
    'Scoring Options': 'Crease lines pressed into thick stock so it folds neatly without the ink cracking.',
    'Drill Hole': 'An optional punched hole — e.g. to hang a sign or fit the piece in a ring binder.',
    'Shape': 'The cut outline of the finished piece.',
    'Radius of Corners': 'How rounded the corners are cut — a larger radius is more rounded, smaller is closer to square.',
    'Die Cut Options': 'A custom shape cut with a metal die, for non-rectangular pieces.',
    'Binding Type': 'How multi-page pieces are held together. Saddle Stitch = stapled on the fold (booklets) · Perfect Bound = glued flat spine (books/catalogs) · Spiral = coil through punched holes (lies flat).',
    'Binding Edge': 'Which edge of the booklet is bound.',
    'Page Count': 'Total pages in the booklet, counting both sides of each sheet.',
    'Blank Envelopes': 'Whether matching blank envelopes are included with your cards, and how many.',
    'Bundling Service': 'Whether finished pieces are banded into set counts (e.g. bundles of 50 or 100) for easier handling.',
    'Shrink Wrapping': 'Optional plastic-wrapping of your pieces in set counts to keep them clean and grouped.',
    'Product Style': 'The specific product variation or garment style.',
    'Product Color': 'The base color of the product or garment.',
    'Product Material': 'The base material the product is made from.',
    'Product Orientation': 'Portrait (tall) or landscape (wide) layout.',
    'Pockets': 'Folder pockets that hold loose inserts or handouts.',
    'Slits': 'Small cuts in a folder pocket that hold a business card.',
    'Business Card Slit': 'Slits cut to hold a business card.',
    'CD Slit': 'A slit cut to hold a CD or disc.',
    'Perforation': 'A line of tiny cuts so part of the piece tears off cleanly (e.g. a coupon or stub).',
    'Numbering': 'Sequential numbers printed on each piece — e.g. for raffle tickets or invoices.',
    'Number of Tabs': 'How many index/divider tabs are included.',
    'Opening Side': 'Which side the envelope or piece opens on.',
    'Window Options': 'A die-cut window (e.g. in an envelope) that shows the address printed beneath.',
    'Variable Data': 'Personalized printing where names or details change on each piece.',
    'White Mask Front': 'A white ink layer printed under the color on clear or metallic stock so the colors stay bright and opaque.',
    'Unwind Options': 'For roll labels: the direction the labels face coming off the roll (positions 1–8), to match your applicator.',
    'EDDM Service Option': 'Every Door Direct Mail — a USPS service that mails to every address on a postal route, no mailing list needed.',
    'Mailing Service': 'Optional service where we address and mail the finished pieces for you.',
    'Sheets Per Pad': 'How many sheets are glued into each notepad.',
    'Hardware': 'Included mounting or hanging hardware.',
    'Shirt Size (Multi-Picker)': 'Choose how many of each garment size you need.',
    'Collection': 'The product line or series this item belongs to.'
  };
  var OPT_HELP = {
    // Colorspec
    '4/4 (4 color both sides)': 'Full color printed on the front AND the back.',
    '4/0 (4 color front)': 'Full color on the front; the back is left blank.',
    '4/1': 'Full color on the front; the back printed in black ink only.',
    // Coating / finish
    'Aqueous Coating': 'A light, water-based satin coat that protects the ink and cuts smudging.',
    'Satin Aqueous': 'A smooth satin protective coat — between matte and gloss.',
    'UV on 4-color side': 'A thick, high-gloss coating cured with UV light — very shiny and scuff-resistant.',
    'UV Coating Front Only': 'High-gloss UV coating on the front only.',
    'Spot UV': 'Glossy clear coating on selected areas only, for contrast.',
    'Matte': 'A smooth, non-glare finish that’s easy to read.',
    'No Coating': 'No protective coat — a natural finish you can write on with pen.',
    'N/A': 'No coating.', 'NA': 'No coating.',
    // Lamination
    'Gloss': 'Shiny, reflective film — makes colors pop.',
    'Gloss Lamination': 'Shiny, reflective film — makes colors pop.',
    'Silk': 'Smooth, soft-touch film with a subtle sheen.',
    'Velvet': 'A soft, velvety matte film — premium feel, no glare.',
    'No Lamination': 'No film — the plain printed surface.',
    // Binding
    'Saddle Stitch': 'Stapled along the fold — best for thinner booklets.',
    'Perfect Bound': 'Pages glued into a flat spine — like a paperback book or thick catalog.',
    'Spiral Bind': 'A coil through punched holes — lies flat and folds all the way around.',
    // Spot UV sides
    'No Spot UV': 'No spot-gloss areas.',
    'Spot UV Front': 'Glossy spot coating on selected front areas.',
    'Spot UV Both Sides': 'Glossy spot coating on selected areas of both sides.',
    'Spot UV Back - Full UV on the Front': 'Full gloss on the front; spot-gloss areas on the back.',
    'Spot UV Back Full UV on the Front': 'Full gloss on the front; spot-gloss areas on the back.',
    // Bundling
    'No Bundling Services': 'Shipped loose, not banded.',
    'Yes Bundles of 50': 'Banded into stacks of 50.',
    'Yes Bundles of 100': 'Banded into stacks of 100.',
    // Folds
    'FLAT - No Folding': 'A flat sheet with no folds.',
    'Half-Fold': 'One fold down the middle — makes 4 panels (a simple card/booklet).',
    'Half-Fold (Vertical)': 'One fold down the vertical center line.',
    'Half-Fold (Horizontal)': 'One fold across the horizontal center line.',
    'Tri-Fold / Letter Fold': 'Two folds into three equal panels — the classic letter / brochure fold.',
    'Z-Fold': 'Two folds in opposite directions forming a “Z” — three panels, accordion-style.',
    'Accordion Fold': 'Several parallel folds in alternating directions, like a fan.',
    'Roll Fold': 'Panels fold inward one over the next, rolling in on themselves.',
    'Gatefold': 'Left and right panels fold in to meet in the middle, like gates.',
    'Double Gatefold': 'A gatefold folded once more for a wide, multi-panel layout.',
    'French Fold': 'Folded in half one way, then in half again the other way (two right-angle folds).',
    'Double Parallel Fold': 'Folded in half, then in half again the same direction — 4 slim panels.',
    'Reverse Double Parallel Fold': 'A double parallel fold made in the opposite order.',
    'Half-Fold and then Tri-Fold': 'Folded in half, then tri-folded — a compact multi-panel piece.',
    'Half Fold and Perf on Fold (Horizontal)': 'Folded in half with a tear-off perforation along the horizontal fold.',
    'Half Fold and Perf on Fold (Vertical)': 'Folded in half with a tear-off perforation along the vertical fold.',
    'Tri-Fold/Letter Fold and Perf on Folds': 'Letter-folded with tear-off perforations on the fold lines.',
    // Scoring
    'Two Scores': 'Two crease lines pressed in for folding.',
    'One Score': 'One crease line pressed in for folding.',
    'No Scoring': 'No crease lines.',
    'No Scoring and Folding': 'Left flat — no scoring or folding.',
    // Drill hole
    'No Hole Punch': 'No hole — a clean, uncut piece.',
    '3 Hole Punch - 1/4"': 'Three ¼-inch holes punched to fit a standard 3-ring binder.',
    // Corners / shape
    'Square': 'Standard square (90°) corners.',
    'Rounded 2 Corners': 'Two corners rounded off, two left square.',
    'Rounded 4 Corners': 'All four corners rounded off.',
    'Round': 'Cut into a full circle.', 'Circle': 'Cut into a full circle.',
    'Oval': 'Cut into an oval.'
  };

  var state = { data: null, sel: [], qty: null, customQty: null };

  fetch(DATA_BASE + '/' + CFG_HANDLE + '.json')
    .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
    .then(function (d) { state.data = d; state.sel = d.attrs.map(function () { return null; }); boot(); })
    .catch(function (e) {
      mount.innerHTML = '<p class="cfg-err">Options are loading slowly — please refresh the page.</p>';
    });

  function validCombos() {
    return state.data.combos.filter(function (row) {
      for (var i = 0; i < state.sel.length; i++)
        if (state.sel[i] !== null && row[i] !== state.sel[i]) return false;
      return true;
    });
  }

  function allowedFor(attrIdx) {
    var ok = {};
    state.data.combos.forEach(function (row) {
      for (var i = 0; i < state.sel.length; i++)
        if (i !== attrIdx && state.sel[i] !== null && row[i] !== state.sel[i]) return;
      ok[row[attrIdx]] = true;
    });
    return ok;
  }

  function currentVariantKey() {
    var rows = validCombos();
    if (rows.length === 1 && state.sel.every(function (s) { return s !== null; }))
      return rows[0][state.sel.length];
    return null;
  }

  function boot() {
    // default: first combo, unless the product JSON names a representative default variant
    var d = state.data, first = d.combos[0];
    if (d.default) {
      for (var i = 0; i < d.combos.length; i++) {
        if (String(d.combos[i][d.combos[i].length - 1]) === String(d.default)) { first = d.combos[i]; break; }
      }
    }
    state.sel = first.slice(0, d.attrs.length);
    if (d.defaultQty) state.qty = d.defaultQty;
    if (d.customSize) { state.w = d.defaultW; state.h = d.defaultH; }
    render();
  }

  // Custom-size (large-format) products are priced per square inch: the tier price is
  // the per-sq-inch rate, so the real price = rate x width x height.
  function areaMult() {
    if (!state.data.customSize) return 1;
    var w = +state.w, hh = +state.h;
    return (w > 0 && hh > 0) ? w * hh : 0;
  }
  function dimsValid() {
    var d = state.data;
    if (!d.customSize) return true;
    var w = +state.w, hh = +state.h;
    return w >= d.minW && w <= d.maxW && hh >= d.minH && hh <= d.maxH;
  }

  function render() {
    var d = state.data, html = '';
    d.attrs.forEach(function (a, ai) {
      var allowed = allowedFor(ai);
      html += '<div class="cfg-row"><label>' + a.label + '</label><select data-ai="' + ai + '">';
      var selLabel = '';
      a.options.forEach(function (o) {
        if (o.label === '-' && !allowed[o.i]) return;
        var dis = allowed[o.i] ? '' : ' disabled';
        var sel = state.sel[ai] === o.i ? ' selected' : '';
        if (state.sel[ai] === o.i) selLabel = o.label;
        html += '<option value="' + o.i + '"' + dis + sel + '>' + o.label +
                (allowed[o.i] ? '' : ' (unavailable)') + '</option>';
      });
      html += '</select>';
      // plain-English help: the selected choice's meaning, else the group's meaning
      var hint = (selLabel && selLabel !== '-' && OPT_HELP[selLabel]) || ATTR_HELP[a.label] || '';
      if (hint) html += '<p class="cfg-hint">' + hint + '</p>';
      html += '</div>';
    });
    if (d.customSize) {
      html += '<div class="cfg-row cfg-size"><label>Finished size <span class="req">*</span></label>' +
        '<div class="cfg-dims">' +
        '<input type="number" id="cfg-w" min="' + d.minW + '" max="' + d.maxW + '" step="0.5" value="' + state.w + '" aria-label="Width in inches">' +
        '<span class="cfg-x">&times;</span>' +
        '<input type="number" id="cfg-h" min="' + d.minH + '" max="' + d.maxH + '" step="0.5" value="' + state.h + '" aria-label="Height in inches">' +
        '<span class="cfg-in">inches</span></div>' +
        '<p class="cfg-hint">Enter width &amp; height (width ' + d.minW + '&ndash;' + d.maxW + '&Prime;, height ' + d.minH + '&ndash;' + d.maxH + '&Prime;). Price scales with the finished area.</p>' +
        '<p class="cfg-dim-err" id="cfg-dim-err" hidden></p></div>';
    }
    html += '<div id="cfg-prices"></div>';
    mount.innerHTML = html;
    mount.querySelectorAll('select').forEach(function (s) {
      s.addEventListener('change', function () {
        var ai = +this.dataset.ai;
        state.sel[ai] = +this.value;
        repair(ai); render();
      });
    });
    if (d.customSize) {
      var wI = document.getElementById('cfg-w'), hI = document.getElementById('cfg-h');
      var onDim = function () { state.w = wI.value; state.h = hI.value; renderPrices(); };
      if (wI) wI.addEventListener('input', onDim);
      if (hI) hI.addEventListener('input', onDim);
    }
    renderPrices();
  }

  // after changing attr ai, fix other attrs to nearest valid combo (keep as many as possible)
  function repair(changedIdx) {
    if (validCombos().length) return;
    var d = state.data, keep = state.sel.slice();
    var best = null, bestScore = -1;
    d.combos.forEach(function (row) {
      if (row[changedIdx] !== keep[changedIdx]) return;
      var score = 0;
      for (var i = 0; i < keep.length; i++) if (row[i] === keep[i]) score++;
      if (score > bestScore) { bestScore = score; best = row; }
    });
    if (best) state.sel = best.slice(0, d.attrs.length);
  }

  function renderPrices() {
    var box = document.getElementById('cfg-prices');
    var vk = currentVariantKey();
    if (!vk) { box.innerHTML = '<p class="cfg-note">Select options to see pricing.</p>'; return; }
    var tiers = state.data.prices[vk];
    if (!tiers || !tiers.length) {
      box.innerHTML = '<p class="cfg-note">Pricing for this configuration is coming soon.</p>';
      return;
    }
    if (state.qty === null || !tiers.some(function (t) { return t[0] === state.qty; }))
      state.qty = tiers[0][0];
    var am = areaMult();
    var h = '<fieldset class="opt-group cfg-qty"><legend>Quantity <span class="req">*</span></legend>';
    tiers.forEach(function (t) {
      var checked = state.qty === t[0] && !state.customQty ? ' checked' : '';
      h += '<label class="opt-row"><input type="radio" name="cfg-qty" value="' + t[0] + '"' + checked + '>' +
           '<span>' + t[0].toLocaleString() + '</span>' +
           '<span class="cfg-unit">' + money(t[1] * am / t[0]) + ' ea</span>' +
           '<span class="opt-fee">' + money(t[1] * am) + '</span></label>';
    });
    var lo = tiers[0][0], hi = tiers[tiers.length - 1][0];
    h += '<div class="cfg-custom"><label>Custom quantity (' + lo.toLocaleString() + '–' + hi.toLocaleString() + ')' +
         '</label><input type="number" id="cfg-custom-qty" min="' + lo + '" max="' + hi + '" placeholder="e.g. 750">' +
         '<span id="cfg-custom-price"></span></div>';
    h += '</fieldset><div class="cfg-total">Total: <strong id="cfg-total"></strong></div>';
    box.innerHTML = h;
    box.querySelectorAll('input[name=cfg-qty]').forEach(function (r) {
      r.addEventListener('change', function () {
        state.qty = +this.value; state.customQty = null;
        document.getElementById('cfg-custom-qty').value = '';
        updateTotal();
      });
    });
    var cq = document.getElementById('cfg-custom-qty');
    cq.addEventListener('input', function () {
      var v = +this.value;
      if (v >= lo && v <= hi) { state.customQty = v; } else { state.customQty = null; }
      updateTotal();
    });
    updateTotal();
  }

  function priceFor(vk, qty) {
    var tiers = state.data.prices[vk];
    for (var i = 0; i < tiers.length; i++) if (tiers[i][0] === qty) return tiers[i][1];
    // 4over-style linear interpolation between surrounding tiers
    var lower = null, upper = null;
    tiers.forEach(function (t) {
      if (t[0] <= qty && (!lower || t[0] > lower[0])) lower = t;
      if (t[0] >= qty && (!upper || t[0] < upper[0])) upper = t;
    });
    if (!lower || !upper) return null;
    if (lower[0] === upper[0]) return lower[1];
    return lower[1] + (qty - lower[0]) / (upper[0] - lower[0]) * (upper[1] - lower[1]);
  }

  function currentTotal() {
    var vk = currentVariantKey();
    if (!vk) return null;
    var qty = state.customQty || state.qty;
    if (!qty) return null;
    var p = priceFor(vk, qty);
    if (p === null) return null;
    var am = areaMult();
    if (state.data.customSize && !(am > 0)) return null;
    return { qty: qty, total: Math.round(p * am * 100) / 100 };
  }

  function updateTotal() {
    var t = currentTotal();
    var valid = dimsValid();
    var err = document.getElementById('cfg-dim-err');
    if (err) {
      var d = state.data;
      if (!valid) {
        err.textContent = 'Enter a width ' + d.minW + '–' + d.maxW + '" and height ' + d.minH + '–' + d.maxH + '".';
        err.hidden = false;
      } else { err.hidden = true; }
    }
    var el = document.getElementById('cfg-total');
    var cp = document.getElementById('cfg-custom-price');
    if (cp) cp.textContent = state.customQty && t && valid ? money(t.total) : '';
    if (el) el.textContent = (t && valid)
      ? money(t.total) + ' · ' + t.qty.toLocaleString() + ' pieces'
        + (state.data.customSize ? ' · ' + state.w + '×' + state.h + '"' : '')
      : '—';
    document.dispatchEvent(new CustomEvent('cfg:total', { detail: (t && valid) ? t : null }));
  }

  // Expose for the product form submit handler
  window.CFG = {
    ready: function () { return !!(state.data && currentVariantKey() && currentTotal() && dimsValid()); },
    spec: function () {
      var d = state.data, props = {};
      d.attrs.forEach(function (a, ai) {
        var o = a.options.find(function (x) { return x.i === state.sel[ai]; });
        props[a.label] = o ? o.label : '';
      });
      if (d.customSize) props['Finished size'] = state.w + '" x ' + state.h + '"';
      var t = currentTotal();
      props['Quantity (pieces)'] = String(t.qty);
      props['Configured price'] = money(t.total);
      return props;
    },
    cartItems: function (fileProps) {
      var t = currentTotal();
      // Decompose the exact price into $100 / $1 / $0.01 denomination lines (each qty
      // <= 2000, the merge limit). All lines share a hidden "_job" token so the active
      // Cart Transform groups them and merges into ONE line priced at the sum. "_spec"
      // is baked into the merged line's title (the merge drops line properties).
      var cents = Math.round(t.total * 100);
      var h = Math.floor(cents / 10000);            // $100 units
      var d = Math.floor((cents % 10000) / 100);    // $1 units
      var c = cents % 100;                          // $0.01 units
      var job = 'job-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
      var label = document.title.split(/[–—|]/)[0].trim();

      var props = Object.assign({}, this.spec(), fileProps || {});
      props._job = job;
      var sp = [];
      if (state.data.customSize) sp.push(state.w + '×' + state.h + '"');
      state.data.attrs.forEach(function (a, ai) {
        var o = a.options.find(function (x) { return x.i === state.sel[ai]; });
        var v = o ? o.label : '';
        if (v && v !== '-') sp.push(v.replace(/\s*\(.*\)$/, ''));
      });
      sp.push(t.qty + ' pcs');
      props._spec = sp.join(' · ');

      var items = [{ id: +PRODUCT_VARIANT, quantity: 1, properties: props }];
      if (h > 0) items.push({ id: +CHARGE_100, quantity: h, properties: { _job: job, 'For': label } });
      if (d > 0) items.push({ id: +CHARGE_1, quantity: d, properties: { _job: job, 'For': label } });
      if (c > 0) items.push({ id: +CHARGE_VARIANT, quantity: c, properties: { _job: job, 'For': label } });
      return items;
    }
  };
})();
